import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class IdbService {
  private readonly platformId = inject(PLATFORM_ID);
  private dbName = 'SokratesRecruitmentDB';
  private dbVersion = 1;
  private storeName = 'formData';
  private db: IDBDatabase | null = null;

  constructor() {
    if (this.isBrowser) {
      void this.initDB();
    }
  }

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private async initDB(): Promise<void> {
    if (!this.isBrowser) {
      return;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error('Failed to open IndexedDB', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event: any) => {
        const db = event.target.result;
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: 'key' });
        }
      };
    });
  }

  private async ensureDB(): Promise<IDBDatabase | null> {
    if (!this.isBrowser) {
      return null;
    }

    if (!this.db) {
      await this.initDB();
    }
    return this.db!;
  }

  async saveFormData(formId: number | string, data: any): Promise<void> {
    try {
      const db = await this.ensureDB();
      if (!db) {
        return;
      }

      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      const formData = {
        key: `form_${formId}`,
        formId: formId,
        data: data,
        timestamp: new Date().toISOString(),
      };

      await new Promise<void>((resolve, reject) => {
        const request = store.put(formData);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error saving form data to IndexedDB:', error);
    }
  }

  async getFormData(formId: number | string): Promise<any | null> {
    try {
      const db = await this.ensureDB();
      if (!db) {
        return null;
      }

      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      
      return new Promise((resolve, reject) => {
        const request = store.get(`form_${formId}`);
        
        request.onsuccess = () => {
          if (request.result) {
            resolve(request.result.data);
          } else {
            resolve(null);
          }
        };
        
        request.onerror = () => {
          console.error('Error getting form data from IndexedDB:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error getting form data from IndexedDB:', error);
      return null;
    }
  }

  async deleteFormData(formId: number | string): Promise<void> {
    try {
      const db = await this.ensureDB();
      if (!db) {
        return;
      }

      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      await new Promise<void>((resolve, reject) => {
        const request = store.delete(`form_${formId}`);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error deleting form data from IndexedDB:', error);
    }
  }

  async clearAllFormData(): Promise<void> {
    try {
      const db = await this.ensureDB();
      if (!db) {
        return;
      }

      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      await new Promise<void>((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error clearing form data from IndexedDB:', error);
    }
  }
}
